import { useEffect, useRef, useState } from "react";
import {
  Box,
  CssBaseline,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Add, CopyAll, Delete, Save } from "@mui/icons-material";
import { ModelRenderer } from "./ModelRenderer";
import { ModelPropertiesPanel } from "./modelpanelparts/ModelPropertiesPanel";
import { createModel, Model } from "../models/Model";
import { MREvents } from "./MREvents";
import { getCreds } from "../contexts/PageContext";
import { ModelSelectBar } from "./common/ModelSelectBar";
import { useNavigate } from "react-router";
import PanelToggles from "../components/PanelToggles";
import { deleteModel, saveModel } from "../api/connection";
import { showConfirmDialog, showSnackbar } from "../components/UniversalDialog";
import { transferToOrg } from "../utils/orgUtils";

const panelConfig = {
  rightWidth: 400,
  leftWidth: 200,
  bottomHeight: 450,
};

export default function ModelEditPanel({
  heightOffset = 0,
  selectedModelId = "",
  rendererKey,
  mr,
}: {
  heightOffset?: number;
  selectedModelId: string;
  rendererKey?: string;
  mr?: ModelRenderer;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  const [boxPosition, setBoxPosition] = useState({ x: 0, y: 0 });
  const [models, setModels] = useState<Model[]>([]);
  const [renderer, setRenderer] = useState<ModelRenderer | null>(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [bottomOpen, setBottomOpen] = useState(false);
  const [renderAreaHeight, setRenderAreaHeight] =
    useState("calc(100vh - 74px)");
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!renderer) return;

    const handleModelSelected = (model: Model) => {
      setSelectedModel(model);
    };

    renderer.onEvent(MREvents.ModelSelected, handleModelSelected);

    return () => {
      renderer.removeListener(MREvents.ModelSelected, handleModelSelected);
    };
  }, [renderer]);
  useEffect(() => {
    if (divRef.current && !renderer) {
      const newRenderer = new ModelRenderer(divRef.current);
      setRenderer(newRenderer);
      let debounceTimeout: any = null;
      const windowResizeHandler = () => {
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
        debounceTimeout = setTimeout(() => {
          setBoxPosition({
            x: boxRef.current?.getBoundingClientRect().left || 0,
            y: boxRef.current?.getBoundingClientRect().top || 0,
          });
          setBoxSize({
            width: boxRef.current?.clientWidth || 0,
            height: boxRef.current?.clientHeight || 0,
          });
        }, 700); // Adjust the delay as needed
      };
      window.addEventListener("resize", windowResizeHandler);
      setBoxSize({
        width: boxRef.current?.clientWidth || 0,
        height: boxRef.current?.clientHeight || 0,
      });
      setBoxPosition({
        x: boxRef.current?.getBoundingClientRect().left || 0,
        y: boxRef.current?.getBoundingClientRect().top || 0,
      });

      return () => {
        window.removeEventListener("resize", windowResizeHandler);
        newRenderer.dispose();
        setRenderer(null);
      };
    }
  }, [rendererKey, divRef]);
  useEffect(() => {
    if (!renderer) {
      return;
    }
    const modelsListener = (models: Model[]) => {
      const sortedModels = [...models].sort((a, b) =>
        a.category.localeCompare(b.category)
      );
      setModels(sortedModels);
    };
    renderer.onEvent(MREvents.ModelsLoaded, modelsListener);
    (async () => {
      await renderer.dataService.waitForLoad();
      const sortedModels = Array.from(renderer.dataService.models.values());
      sortedModels.sort((a, b) => a.category.localeCompare(b.category));
      setModels(sortedModels);
      const { org } = getCreds();
      const sm = renderer.dataService.models.get(selectedModelId) || {
        ...createModel(),
        orgId: org._id,
      };
      renderer.emitEvent(MREvents.ModelSelected, sm);
    })().catch(console.error);
    return () => {
      renderer.off(MREvents.ModelsLoaded, modelsListener);
    };
  }, [selectedModelId, renderer]);
  useEffect(() => {
    // console.log('boxSize changed:', boxSize);
    const renderHeightOffset =
      heightOffset + (bottomOpen ? panelConfig.bottomHeight : 0);
    setRenderAreaHeight(`calc(100vh - ${renderHeightOffset}px)`);
    setTimeout(() => {
      // wut
      if (renderer) {
        const height = boxRef.current?.clientHeight || 0;
        const width = boxRef.current?.clientWidth || 0;
        setBoxSize({ width, height });
        setBoxPosition({
          x: boxRef.current?.getBoundingClientRect().left || 0,
          y: boxRef.current?.getBoundingClientRect().top || 0,
        });

        renderer.setSize(width, height);
      }
    }, 500);
  }, [rightOpen, leftOpen, bottomOpen, boxSize]);

  const handleLeftToggle = () => setLeftOpen(!leftOpen);
  const handleRightToggle = () => setRightOpen(!rightOpen);
  const handleBottomToggle = () => setBottomOpen(!bottomOpen);
  const handleSave = async () => {
    if (!selectedModel || !renderer) return;

    const m = selectedModel;
    m.settings.lastEdit = Date.now();
    m.settings.cameraPosition = renderer.getCameraPosition();
    const isNew = !m._id;
    await saveModel(m);

    if (isNew) {
      renderer.dataService.models.set(m._id, m);
      navigate(`/models/${m._id}`);
    }
  };

  const handleDelete = async () => {
    if (!selectedModel?._id || !renderer) return;

    const yes = await showConfirmDialog(
      "CONFIRM DELETION",
      "Are you sure you want to delete this model?"
    );

    if (yes) {
      await deleteModel(selectedModel);
      renderer.dataService.models.delete(selectedModel._id);
      navigate(`/models/new`);
    }
  };

  const handleNew = () => {
    navigate(`/models/new`);
  };
  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 65,
          }}
        >
          <Typography
            sx={{ paddingLeft: "24px", fontSize: "20px" }}
            variant="h6"
          >
            Models
          </Typography>
        </Box>

        <Box
          sx={{
            width: panelConfig.rightWidth,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            paddingRight: "10px",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Tooltip title="Save Model">
              <IconButton onClick={handleSave} size="small">
                <Save />
              </IconButton>
            </Tooltip>

            <Tooltip title="New Model">
              <IconButton onClick={handleNew} size="small">
                <Add sx={{ fontSize: "30px" }} />
              </IconButton>
            </Tooltip>
            {selectedModel && selectedModel._id && (
              <>
                <Tooltip title="Delete Model">
                  <IconButton onClick={handleDelete} color="error" size="small">
                    <Delete />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy To Another Organization">
                  <IconButton onClick={async ()=>{
                    const success = await transferToOrg(selectedModel!, true);
                    if (success) {
                      showSnackbar('Model copied to target organization', 'success');
                    }
                  }} color="error" size="small">
                    <CopyAll />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          <PanelToggles
            leftOpen={leftOpen}
            rightOpen={rightOpen}
            bottomOpen={bottomOpen}
            onLeftToggle={handleLeftToggle}
            onRightToggle={handleRightToggle}
            onBottomToggle={handleBottomToggle}
            rightTooltip={{ open: "Close Info", closed: "Open Info" }}
          />
        </Box>

        {/* Panel toggles on the right */}
      </Box>

      <Box
        sx={{
          display: "flex",
          height: renderAreaHeight,
          margin: 0,
          padding: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: leftOpen ? panelConfig.leftWidth : 0,
            overflow: "hidden",
          }}
        >
          <ModelSelectBar
            models={models}
            selectedModelId={selectedModelId}
            onModelSelect={(model) => navigate(`/models/${model._id}`)}
          />
        </Box>
        <Box
          ref={boxRef}
          sx={{
            margin: 0,
            padding: 0,
            flexGrow: 1,
            overflow: "hidden",
            height: renderAreaHeight,
          }}
        >
          <div
            ref={divRef}
            style={{
              height: renderAreaHeight,
              overflow: "hidden",
              padding: 0,
              margin: 0,
            }}
            tabIndex={0}
          />
        </Box>
        <Box
          sx={{
            width: rightOpen ? panelConfig.rightWidth : 0,
          }}
        >
          {renderer && <ModelPropertiesPanel mr={renderer} />}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          height: bottomOpen ? panelConfig.bottomHeight : 0,
          width: "100%",
        }}
      >
        Scenarios
      </Box>
    </>
  );
}
